import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Copy, Download, Building2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";

interface MediaItem {
  id?: string;
  url: string;
  type: string;
  storagePath?: string; // only available in user-scoped mode
}

interface MediaLibraryProps {
  onMediaSelect?: (url: string) => void;
  selectable?: boolean;
}

export const MediaLibrary = ({ onMediaSelect, selectable = false }: MediaLibraryProps) => {
  const { orgId } = useOrgPermissions();
  const [mediaFiles, setMediaFiles] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchMedia();
  }, [orgId]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (orgId) {
        // Org-scoped: query post_media table via posts.org_id
        const { data: posts } = await (supabase
          .from("posts")
          .select("post_media(id, media_url, media_type)")
          .eq("org_id", orgId) as any);

        const items: MediaItem[] = ((posts ?? []) as any[])
          .flatMap((p: any) => p.post_media ?? [])
          .filter((m: any) => m?.media_url)
          .map((m: any) => ({ id: m.id, url: m.media_url, type: m.media_type ?? "image" }));

        setMediaFiles(items);
      } else {
        // User-scoped: list from storage bucket
        const { data, error } = await supabase
          .storage
          .from("post-media")
          .list(user.id, { limit: 100, sortBy: { column: "created_at", order: "desc" } });

        if (error) throw error;

        const items: MediaItem[] = (data ?? []).map(file => {
          const { data: { publicUrl } } = supabase
            .storage
            .from("post-media")
            .getPublicUrl(`${user.id}/${file.name}`);
          return {
            url: publicUrl,
            type: file.name.match(/\.(mp4|webm|mov)$/i) ? "video" : "image",
            storagePath: `${user.id}/${file.name}`,
          };
        });
        setMediaFiles(items);
      }
    } catch (error) {
      console.error("Error fetching media:", error);
      toast({ title: "Error", description: "Failed to load media library", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ title: "Copied", description: "Media URL copied to clipboard" });
  };

  const handleDownload = (url: string) => {
    window.open(url, "_blank");
  };

  const handleDelete = async (item: MediaItem) => {
    if (!item.storagePath) {
      toast({ title: "Cannot delete", description: "Deletion is only available in personal media view.", variant: "destructive" });
      return;
    }
    try {
      const { error } = await supabase.storage.from("post-media").remove([item.storagePath]);
      if (error) throw error;
      toast({ title: "Deleted", description: "Media deleted successfully" });
      fetchMedia();
    } catch (error) {
      console.error("Error deleting media:", error);
      toast({ title: "Error", description: "Failed to delete media", variant: "destructive" });
    }
  };

  if (loading) {
    return <div className="text-center p-8">Loading media library...</div>;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        {orgId && (
          <Badge variant="secondary" className="gap-1 text-xs">
            <Building2 className="w-3 h-3" />
            Organisation
          </Badge>
        )}
      </div>
      {orgId && (
        <p className="text-justify text-xs text-muted-foreground mb-4">
          Showing all media uploaded by organisation members across all posts.
        </p>
      )}
      <ScrollArea className="h-[500px]">
        {mediaFiles.length === 0 ? (
          <div className="text-center text-muted-foreground p-8">
            {orgId
              ? "No media uploaded by organisation members yet."
              : "No media files yet. Upload some media when creating posts!"}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {mediaFiles.map((item, index) => (
              <div key={item.id ?? index} className="relative group">
                <div
                  className={`aspect-square rounded-lg overflow-hidden border-2 ${
                    selectable ? "cursor-pointer hover:border-primary" : ""
                  }`}
                  onClick={() => selectable && onMediaSelect?.(item.url)}
                >
                  {item.type === "image" ? (
                    <img src={item.url} alt="Media" className="w-full h-full object-cover" />
                  ) : (
                    <div className="relative w-full h-full bg-muted">
                      <video src={item.url} className="w-full h-full object-cover" controls preload="metadata" />
                      <div className="absolute top-2 left-2">
                        <Badge variant="secondary" className="text-xs">Video</Badge>
                      </div>
                    </div>
                  )}
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => handleCopyUrl(item.url)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => handleDownload(item.url)}>
                    <Download className="h-4 w-4" />
                  </Button>
                  {!orgId && (
                    <Button size="icon" variant="destructive" className="h-8 w-8" onClick={() => handleDelete(item)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </Card>
  );
};
